/**
 * Panel — el esqueleto de la aplicación con sesión y organización.
 *
 * Diseño aprobado por el propietario el 2026-09-21: menú lateral claro con la
 * marca y la empresa arriba, las secciones agrupadas ("Menú" y
 * "Preferencias"), el buscador en una barra superior y "Cerrar sesión" al
 * pie. Datos fiscales, Seguridad y Mi cuenta viven DENTRO de Configuración.
 * En el móvil el menú lateral desaparece y aparece una barra inferior con un
 * botón central para crear un presupuesto.
 *
 * Sólo tiene las secciones que EXISTEN. Un menú con "Facturas" o "Reportes"
 * que llevan a pantallas vacías prometería algo que la API no hace; esas
 * entradas se añaden con sus módulos.
 *
 * `OD-0017 D-04/D-05`: navegación con `<nav>` y `aria-current`, objetivos de
 * 44 px, y la sección elegida recibe el foco para que un lector de pantalla
 * anuncie el cambio.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  api,
  apiCompras,
  apiEmpresas,
  apiPanelAsesoria,
  noLeidosDeLaConversacion,
  type EstadoOportunidad,
  type FiltroPendientes,
  type FiltrosCompras,
  type FiltrosPresupuestosProveedor,
  type PanelDeAsesoria,
  type PlanDePago,
  type PruebaDeBusiness,
  type Sesion,
  type UsoFacturas,
} from "../api/cliente";
import { CampanaAvisos, calcularAvisos, type Aviso, type DestinoAviso } from "./Avisos";
import type { RespuestaSii } from "../ui/pasos-del-alta";
import { t } from "../i18n/es";
import { PantallaAutorizacionAeat } from "./PantallaAutorizacionAeat";
import { PuertaAutorizacion } from "./PuertaAutorizacion";
import { PantallaCuenta } from "./PantallaCuenta";
import { PantallaEquipo } from "./PantallaEquipo";
import { AsesoriaQueHaEntrado, InvitacionDeAsesoria, InvitacionDeTuCliente, PantallaAsesoria } from "./PantallaAsesoria";
import { FranjaDeAsesoria, PantallaPanelAsesoria } from "./PantallaPanelAsesoria";
import { AvisoDeConversacion, ConversacionConLaAsesoria } from "./ConversacionConLaAsesoria";
import { AvisoDeOtraPestana } from "./CambioDeEmpresa";
import { abrirPanelPendiente, almacenDeLaPestana, vaciarCache } from "../ui/panel-de-asesoria";
import { PantallaCliente } from "./PantallaCliente";
import { PantallaClientes } from "./PantallaClientes";
import { PantallaEmisor } from "./PantallaEmisor";
import { PantallaConfiguracion, PantallaInicio, type EstadoEmisor } from "./PantallaInicio";
import { PantallaProducto } from "./PantallaProducto";
import { PantallaProductos } from "./PantallaProductos";
import { PantallaOportunidad } from "./PantallaOportunidad";
import { PantallaOportunidades } from "./PantallaOportunidades";
import { PantallaTareas } from "./Actividades";
import { PantallaPresupuesto } from "./PantallaPresupuesto";
import { PantallaPresupuestos } from "./PantallaPresupuestos";
import { PantallaPlan } from "./PantallaPlan";
import { PantallaSeguridad } from "./PantallaSeguridad";
import { BuscadorGlobal } from "./BuscadorGlobal";
import { abrePagoDirecto, planesQueOfrecer } from "./oferta-de-plan";
import { olvidarPlanElegido } from "../enlaces/plan-elegido";
import { PantallaFacturas } from "./PantallaFacturas";
import { PantallaCobrosPendientes } from "./PantallaCobrosPendientes";
import { PantallaFacturasImportadas } from "./PantallaFacturasImportadas";
import { PantallaImportar } from "./PantallaImportar";
import { PantallaInformesFiscales } from "./PantallaInformesFiscales";
import { PantallaFactura } from "./PantallaFactura";
import { PantallaCompras, SIN_FILTROS_COMPRAS, SIN_FILTROS_PRESUPUESTOS, type PestanaCompras } from "./PantallaCompras";
import { PantallaCompra, type DesdePresupuesto } from "./PantallaCompra";
import { PantallaPresupuestoProveedor } from "./PantallaPresupuestoProveedor";
import { PantallaProveedor } from "./PantallaProveedor";
import { PantallaSoporte } from "./PantallaSoporte";
import { Icono, type NombreIcono } from "../ui/Iconos";
import { fechaEnDias } from "../ui/jose";
import { contarDeHoy, contarVencidas } from "../ui/tareas-vencidas";
import { diaLegible } from "../ui/formato";
import { SelectorDeEmpresa } from "./SelectorDeEmpresa";

/** "mas" sólo existe en el móvil: la lista de lo que no cabe en la barra inferior. */
type Seccion =
  | "inicio"
  | "clientes"
  | "productos"
  | "presupuestos"
  | "facturas"
  | "compras"
  | "oportunidades"
  | "tareas"
  | "informes"
  // OD-0063 D-06: el panel de la gestoria. Solo con el perfil de asesoria activo.
  | "gestoria"
  | "configuracion"
  | "soporte"
  | "mas";

/** Apartados de Configuración. */
type Ajuste = "pasos" | "emisor" | "autorizacion" | "importar" | "seguridad" | "cuenta" | "equipo" | "plan" | "asesoria";

/** Dentro de Clientes: la lista, un alta nueva o la ficha de uno. */
type VistaClientes = { readonly tipo: "lista" } | { readonly tipo: "nuevo" } | { readonly tipo: "ficha"; readonly id: string };

/** Igual para Productos y Presupuestos. */
type VistaProductos = VistaClientes;
type VistaPresupuestos = VistaClientes;
/** OD-0051: dentro de Facturas, tambien la lista de cobros pendientes. */
// OD-0062 E-11: desde la campana, Cobros pendientes llega con «Vencidas» elegido.
type VistaFacturas = VistaClientes | { readonly tipo: "pendientes"; readonly filtro?: FiltroPendientes } | { readonly tipo: "importadas" };
/** OD-0048: el alta puede llegar desde la ficha de un cliente, con el puesto. */
/**
 * OD-0054: las listas de Compras, una compra (nueva o ya anotada) o un proveedor.
 * OD-0055: tambien un presupuesto de proveedor; una compra abierta desde el
 * presupuesto vuelve a el, y la nueva nace con el elegido.
 */
type VistaCompras =
  | { readonly tipo: "lista"; readonly pestana: PestanaCompras }
  | {
      readonly tipo: "compra";
      readonly id: string | null;
      readonly proveedorId: string | null;
      readonly alPago?: boolean;
      readonly presupuesto?: DesdePresupuesto;
      readonly volverA?: string;
    }
  | { readonly tipo: "proveedor"; readonly id: string | null }
  | { readonly tipo: "presupuesto"; readonly id: string | null };
type VistaOportunidades =
  | { readonly tipo: "lista" }
  | { readonly tipo: "nuevo"; readonly clienteId: string | null }
  // OD-0058-ENMIENDA-02 G-08: José invita a cualificar al crearla o al moverla sin cualificar.
  | { readonly tipo: "ficha"; readonly id: string; readonly invitar?: "creada" | "movida" };

type Opcion = { readonly id: Seccion; readonly etiqueta: string; readonly icono: NombreIcono };

const MENU: readonly Opcion[] = [
  { id: "inicio", etiqueta: t.panel.inicio, icono: "inicio" },
  { id: "presupuestos", etiqueta: t.panel.presupuestos, icono: "presupuestos" },
  { id: "facturas", etiqueta: t.panel.facturas, icono: "facturas" },
  // Facturas de compra (OD-0054), junto a las emitidas. Aparece en todos los
  // planes: leer no exige el plan (D-11), y fuera de Business la pantalla
  // explica que anotar es de Business.
  { id: "compras", etiqueta: t.panel.compras, icono: "compras" },
  { id: "clientes", etiqueta: t.panel.clientes, icono: "clientes" },
  { id: "productos", etiqueta: t.panel.productos, icono: "productos" },
  // CRM V1 de Business (OD-0048 D-07). Aparece en todos los planes: fuera de
  // Business su pantalla explica que es de Business (D-04).
  { id: "oportunidades", etiqueta: t.panel.oportunidades, icono: "oportunidades" },
  // Tareas y actividades (OD-0050 D-07), con el mismo criterio: fuera de
  // Business su pantalla explica que es de Business.
  { id: "tareas", etiqueta: t.panel.tareas, icono: "tareas" },
  // Informes fiscales (OD-0041), con el mismo criterio: fuera de Business su
  // pantalla explica que es de Business, una vez y sin cifras (D-13).
  { id: "informes", etiqueta: t.panel.informes, icono: "informes" },
];

const PREFERENCIAS: readonly Opcion[] = [
  { id: "configuracion", etiqueta: t.panel.configuracion, icono: "ajustes" },
  { id: "soporte", etiqueta: t.panel.soporte, icono: "soporte" },
];

const AJUSTES: ReadonlyArray<{ readonly id: Ajuste; readonly etiqueta: string; readonly icono: NombreIcono }> = [
  { id: "pasos", etiqueta: t.panel.primerosPasos, icono: "ajustes" },
  { id: "emisor", etiqueta: t.panel.emisor, icono: "emisor" },
  // TDS-CORE Parte 4: el paso que hoy impide emitir.
  { id: "autorizacion", etiqueta: t.panel.autorizacion, icono: "emisor" },
  // OD-0057: importar clientes, productos, proveedores y facturas de otro programa.
  { id: "importar", etiqueta: t.importar.pestana, icono: "importar" },
  { id: "seguridad", etiqueta: t.panel.seguridad, icono: "seguridad" },
  { id: "cuenta", etiqueta: t.panel.cuenta, icono: "cuenta" },
  // OD-0060: quien entra en la empresa. Solo sale con users.manage (abajo).
  { id: "equipo", etiqueta: t.panel.equipo, icono: "clientes" },
  // OD-0014 A-10: contratar y gestionar el pago. Vive en Configuración porque
  // es del titular de la organización, no del trabajo del día.
  { id: "plan", etiqueta: t.panel.plan, icono: "plan" },
];

/**
 * `OD-0063`: la pestaña del programa para gestorías. Aparte de `AJUSTES` porque
 * solo sale para OWNER y ADMIN (`users.manage`) y con el programa encendido
 * (D-11 A, solo staging): en producción no hay pestaña que lleve a una pantalla
 * que diga «todavía no».
 */
const AJUSTE_ASESORIA = { id: "asesoria", etiqueta: t.panel.asesoria, icono: "clientes" } as const;

/**
 * `OD-0063` D-06: «Empresas cliente», el panel de la gestoría. Aparte del
 * `MENU` porque solo sale con el perfil de asesoría ACTIVO, y lo dice la API
 * (`GET /asesoria/panel`); y porque las posiciones del `MENU` las usa la barra
 * del móvil.
 */
const OPCION_GESTORIA: Opcion = { id: "gestoria", etiqueta: t.panel.empresasCliente, icono: "clientes" };

/** "FREE_V1" -> "Plan Gratis". Una versión desconocida no se muestra. */
function nombrePlan(version: string | null): string | null {
  if (version === null) return null;
  const clave = (Object.keys(t.panel.planes) as Array<keyof typeof t.panel.planes>).find((k) => version.startsWith(`${k}_`));
  return clave === undefined ? null : t.panel.planes[clave];
}

/**
 * «Pasar a Factura+» y «Pasar a Business», a la vista (propietario,
 * 2026-10-19). Antes solo se llegaba por Configuración > Plan y pago.
 *
 * Qué botones salen lo decide el servidor (`oferta-de-plan.ts`). Pulsar uno
 * para CONTRATAR pide el pago a la API en el acto y va derecho a Stripe
 * (propietario, 2026-10-21); para SUBIR con suscripción viva, o si la API se
 * niega, lleva a la pantalla del plan con ese plan elegido.
 *
 * El mismo bloque va en dos sitios: al pie del menú lateral en el escritorio,
 * y arriba del Inicio en el móvil, donde no hay menú lateral. En el lateral va
 * COMPACTO -solo el nombre de cada plan, lado a lado-: con el lema debajo
 * medía 240 px y, en una pantalla de portátil de 820 px de alto, empujaba
 * el propio bloque y «Cerrar sesión» por debajo del borde.
 */
function MejoraDePlan({
  oferta,
  variante,
  abriendo,
  onElegir,
}: {
  oferta: readonly PlanDePago[];
  variante: "lateral" | "movil";
  /** El plan cuyo pago se está pidiendo, o `null`. Mientras, nada se pulsa. */
  abriendo: PlanDePago | null;
  onElegir: (plan: PlanDePago) => void;
}) {
  if (oferta.length === 0) return null;
  const compacto = variante === "lateral";
  return (
    <section className={`mejora-plan sin-imprimir mejora-plan-${variante}`} aria-label={t.panel.mejora.region}>
      <p className="mejora-plan-titulo">{t.panel.mejora.titulo}</p>
      <ul className="mejora-plan-lista">
        {oferta.map((p) => (
          <li key={p}>
            <button
              type="button"
              className="mejora-plan-opcion"
              // El nombre accesible CONTIENE el texto visible (WCAG 2.5.3).
              aria-label={compacto && abriendo !== p ? t.panel.mejora.pasarA[p] : undefined}
              aria-busy={abriendo === p}
              disabled={abriendo !== null}
              onClick={() => onElegir(p)}
            >
              {abriendo === p ? (
                <span className="mejora-plan-nombre">{t.plan.abriendoPago}</span>
              ) : compacto ? (
                <span className="mejora-plan-nombre">{t.panel.mejora.nombre[p]}</span>
              ) : (
                <>
                  <span className="mejora-plan-texto">
                    <span className="mejora-plan-nombre">{t.panel.mejora.pasarA[p]}</span>
                    <span className="mejora-plan-lema">{t.panel.mejora.lema[p]}</span>
                  </span>
                  <Icono nombre="siguiente" />
                </>
              )}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * La franja de la prueba de Business — `OD-0064` D-07. Arriba, en todas las
 * pantallas menos la del plan, que lo cuenta entero.
 *
 * Cuántos días quedan y hasta qué día lo dice el SERVIDOR, en hora de Madrid:
 * aquí no se calcula ninguna fecha. Lo de elegir clientes sale solo si se
 * sabe que hará falta: con los clientes que tiene y los que admite el Free al
 * que bajaría, los dos leídos de la API. Sin uno de los dos, no se dice nada.
 */
function FranjaDePrueba({
  prueba,
  clientes,
  onElegirPlan,
}: {
  prueba: PruebaDeBusiness;
  clientes: number | null;
  onElegirPlan: () => void;
}) {
  const tendraQueElegir = clientes !== null && prueba.clientesFree !== null && clientes > prueba.clientesFree;
  return (
    <section className="aviso aviso-info franja-prueba sin-imprimir" aria-label={t.panel.prueba.region}>
      <p>
        <strong>{t.panel.prueba.quedan(prueba.diasRestantes, diaLegible(prueba.ultimoDia))}</strong>
      </p>
      <p>{t.panel.prueba.despues}</p>
      {tendraQueElegir && clientes !== null && prueba.clientesFree !== null ? <p>{t.panel.prueba.elegir(clientes, prueba.clientesFree)}</p> : null}
      <p>
        {/* Hallazgo 16: un boton del sistema, no el nativo del navegador. */}
        <button type="button" className="boton boton-secundario" onClick={onElegirPlan}>
          {t.panel.prueba.elegirPlan}
        </button>
      </p>
    </section>
  );
}

/** Dos iniciales para el círculo de la empresa; sin nombre, un punto neutro. */
function iniciales(nombre: string | null): string {
  if (nombre === null) return "·";
  const palabras = nombre.trim().split(/\s+/).filter((p) => p.length > 0);
  return palabras
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("");
}

export function Panel({
  sesion,
  hayConexion,
  onSalir,
  abrirPlan = false,
  vueltaDePago = null,
  planElegido = null,
  onCambioDeEmpresa,
}: {
  sesion: Sesion;
  hayConexion: boolean;
  onSalir: () => void;
  /** OD-0060 D-04: la sesión ya está en otra empresa; todo lo de aquí es de la anterior. */
  onCambioDeEmpresa: () => void;
  /** Se ha entrado por `/plan`: la vuelta de Stripe aterriza ahí. */
  abrirPlan?: boolean;
  vueltaDePago?: "confirmando" | "cancelado" | null;
  /** El plan elegido en faktelio.com: se viene a pagarlo (`enlaces/plan-elegido.ts`). */
  planElegido?: PlanDePago | null;
}) {
  // D-05: al volver de un cliente con «Volver al panel», se abre el panel.
  const [seccion, setSeccion] = useState<Seccion>(() =>
    abrirPlan ? "configuracion" : abrirPanelPendiente(almacenDeLaPestana()) ? "gestoria" : "inicio",
  );
  const [ajuste, setAjuste] = useState<Ajuste>(abrirPlan ? "plan" : "pasos");
  // OD-0060: la pestaña de Equipo solo para quien lo gestiona. No concede
  // nada: la API vuelve a pedir users.manage en cada petición.
  const gestionaEquipo = sesion.capabilities.includes("users.manage");
  const [estadoEmisor, setEstadoEmisor] = useState<EstadoEmisor>(null);
  // OD-0035: la respuesta del SII, para LA lista de pasos. `undefined` = no se sabe.
  const [siiEmisor, setSiiEmisor] = useState<RespuestaSii>(undefined);
  // La autorización y el segundo factor, para la misma lista. `null` = no se sabe.
  const [estadoAutorizacion, setEstadoAutorizacion] = useState<string | null>(null);
  const [mfaActivo, setMfaActivo] = useState<boolean | null>(null);
  const [nombreEmpresa, setNombreEmpresa] = useState<string | null>(null);
  /*
    El nombre que la persona puso a su organización al darse de alta
    (auditoría del 2026-10-29, hallazgo 7): se pedía y no salía en ningún
    sitio, porque la barra enseñaba la razón social. Es el mismo nombre que
    usa el selector de empresas. Sin él, la razón social; sin las dos, «Tu
    empresa».
  */
  const [nombreOrganizacion, setNombreOrganizacion] = useState<string | null>(null);
  const [tieneClientes, setTieneClientes] = useState<boolean | null>(null);
  const [tieneFacturaEmitida, setTieneFacturaEmitida] = useState<boolean | null>(null);
  const [usoFacturas, setUsoFacturas] = useState<UsoFacturas | null>(null);
  const [vistaClientes, setVistaClientes] = useState<VistaClientes>({ tipo: "lista" });
  const [vistaProductos, setVistaProductos] = useState<VistaProductos>({ tipo: "lista" });
  const [vistaPresupuestos, setVistaPresupuestos] = useState<VistaPresupuestos>({ tipo: "lista" });
  const [vistaFacturas, setVistaFacturas] = useState<VistaFacturas>({ tipo: "lista" });
  const [vistaOportunidades, setVistaOportunidades] = useState<VistaOportunidades>({ tipo: "lista" });
  // La pestaña de Oportunidades sobrevive a abrir una y volver: desde «Ganadas»
  // se volvía a «Abiertas» (prueba del propietario en staging, 2026-10-25).
  const [pestanaOportunidades, setPestanaOportunidades] = useState<EstadoOportunidad>("OPEN");
  // Y lo que se busca en ellas (OD-0048-ENMIENDA-02 F-03), por lo mismo.
  const [busquedaOportunidades, setBusquedaOportunidades] = useState("");
  const [vistaCompras, setVistaCompras] = useState<VistaCompras>({ tipo: "lista", pestana: "recibidas" });
  // Los filtros de las recibidas sobreviven a abrir una compra y volver.
  const [filtrosCompras, setFiltrosCompras] = useState<FiltrosCompras>(SIN_FILTROS_COMPRAS);
  const [filtrosPresupuestosProveedor, setFiltrosPresupuestosProveedor] = useState<FiltrosPresupuestosProveedor>(SIN_FILTROS_PRESUPUESTOS);
  // D-03: el presupuesto nuevo que abre una oportunidad ganada. Solo rellena.
  const [presupuestoInicial, setPresupuestoInicial] = useState<{
    readonly clienteId: string;
    readonly concepto: string;
    readonly oportunidadId: string;
  } | null>(null);
  const [saliendo, setSaliendo] = useState(false);
  const [plan, setPlan] = useState<string | null>(null);
  // Inicio cambia en Business (oportunidades en vez de límites). `null`
  // mientras no se sabe: entonces Inicio se queda como en los demás planes.
  const [esBusiness, setEsBusiness] = useState<boolean | null>(null);
  const [avisos, setAvisos] = useState<readonly Aviso[] | null>(null);
  // Lo que se ofrece a la vista para cambiar de plan. Vacío mientras no se
  // sabe, y vacío para quien no es OWNER (la API contesta 403).
  const [oferta, setOferta] = useState<readonly PlanDePago[]>([]);
  // Si esa oferta es para contratar (Checkout) y puede ir derecha a Stripe.
  const [ofertaDirecta, setOfertaDirecta] = useState(false);
  // OD-0064 D-07: la prueba de Business en curso y los clientes que ocupan
  // plaza, para la franja. `null` = no hay prueba, o no se sabe.
  const [prueba, setPrueba] = useState<PruebaDeBusiness | null>(null);
  /*
    Auditoría del onboarding (hallazgo 10): durante la prueba ya se está EN
    Business, y «Pasar a Business» al lado de la franja que lo dice parecía
    que la prueba no valía. Elegir plan sigue a un clic en la franja.
  */
  const ofertaALaVista = prueba === null ? oferta : oferta.filter((p) => p !== "BUSINESS");
  const [clientesOcupados, setClientesOcupados] = useState<number | null>(null);
  const [abriendoPago, setAbriendoPago] = useState<PlanDePago | null>(null);
  // Lo que contestó la API si no abrió el pago: la pantalla del plan lo enseña.
  const [falloDelPago, setFalloDelPago] = useState<string | null>(null);
  // El plan pulsado en ese bloque: la pantalla del plan lo pone primero, igual
  // que con el plan elegido en faktelio.com.
  const [planPulsado, setPlanPulsado] = useState<PlanDePago | null>(null);
  /**
   * Si el diálogo del alta se ve — `OD-0039` E-11 (2026-09-30).
   *
   * ESTADO DE INTERFAZ, y nada más. No dice si la configuración está
   * completa, no se guarda y no se consulta en ninguna decisión de negocio:
   * lo que decide si se puede emitir es el control previo del servidor.
   *
   * Empieza abierto —el aviso se da al entrar, que es de lo que iba `OD-0039`—
   * y una vez cerrado NO vuelve solo al cambiar de pantalla. Vuelve cuando se
   * pide algo que de verdad lo necesita: intentar emitir una factura.
   */
  /*
    Salvo a quien llega a PAGAR el plan que eligio en faktelio.com: el dialogo
    taparia la pantalla de pago, que es justo a lo que viene, y el pago no
    depende de la autorizacion ante la AEAT. Vuelve igual que siempre, al
    intentar emitir.
  */
  const [puertaAbierta, setPuertaAbierta] = useState(!(abrirPlan && planElegido !== null));

  // Estado REAL del emisor para Inicio y para la tarjeta de la empresa. Si
  // falla, se queda en "no se sabe" (null) en vez de afirmar que falta.
  const cargarEmisor = useCallback(async (): Promise<void> => {
    const r = await api.obtenerEmisor();
    if (!r.ok) return;
    const e = r.datos.emisor;
    setEstadoEmisor(e === null ? "sin-datos" : e.confirmedAt === null ? "sin-confirmar" : "confirmado");
    setSiiEmisor(e === null ? null : e.siiStatus);
    setNombreEmpresa(e === null ? null : e.legalName);
  }, []);

  useEffect(() => {
    let vigente = true;
    void apiEmpresas.listar().then((r) => {
      if (!vigente || !r.ok) return;
      setNombreOrganizacion(r.datos.find((e) => e.activa)?.nombre ?? null);
    });
    return () => {
      vigente = false;
    };
  }, []);

  // Paso 6: hecho si la organización ocupa al menos una plaza de cliente.
  const cargarClientes = useCallback(async (): Promise<void> => {
    const r = await api.plazasClientes();
    if (r.ok) setTieneClientes(r.datos.used > 0);
  }, []);

  useEffect(() => {
    void cargarEmisor();
    void cargarClientes();
  }, [cargarEmisor, cargarClientes]);

  // OD-0063: ¿hay programa para gestorías en este entorno? Solo se pregunta si
  // el rol puede gestionarlo (`gestionaEquipo`, arriba); la respuesta que
  // cuenta es la de la API.
  const [programaAsesorias, setProgramaAsesorias] = useState(false);
  useEffect(() => {
    if (!gestionaEquipo) return;
    void api.perfilAsesoria().then((r) => setProgramaAsesorias(r.ok && r.datos.programa));
  }, [gestionaEquipo]);
  // OD-0067 D-05: la conversacion con la gestoria. La tienen quienes gestionan
  // la empresa, tambien BILLING, que no ve el resto de Asesoria: por eso abre
  // la pestana aunque el programa no se haya preguntado. Sin plan, sin rol o
  // sin gestoria con acceso, la API no contesta 200 y no hay nada.
  const hablaConLaAsesoria = sesion.capabilities.includes("advisor_conversation.write");
  const [conversacion, setConversacion] = useState<{ readonly noLeidos: number } | null>(null);
  useEffect(() => {
    if (!hablaConLaAsesoria) return;
    void noLeidosDeLaConversacion().then((r) => setConversacion(r.ok ? r.datos : null));
  }, [hablaConLaAsesoria, seccion]);
  const ajustes = programaAsesorias || conversacion !== null ? [...AJUSTES, AJUSTE_ASESORIA] : AJUSTES;

  // OD-0063 D-06: el panel de la gestoria, si esta empresa ES una con el perfil
  // activo. Una lectura de GLOBAL_DB al entrar; sin perfil no hay pestana, y
  // si falla, tampoco: no se ensena una seccion que no se sabe si existe.
  const [panelAsesoria, setPanelAsesoria] = useState<PanelDeAsesoria | null>(null);
  const [panelAsesoriaLeido, setPanelAsesoriaLeido] = useState(false);
  useEffect(() => {
    void apiPanelAsesoria.filas().then((r) => {
      setPanelAsesoria(r.ok && r.datos.perfilActivo ? r.datos : null);
      setPanelAsesoriaLeido(true);
    });
  }, []);
  const conPanelAsesoria = panelAsesoria !== null;
  // Se pidio abrir el panel y esta empresa no lo tiene: al Inicio.
  useEffect(() => {
    if (panelAsesoriaLeido && !conPanelAsesoria && seccion === "gestoria") setSeccion("inicio");
  }, [panelAsesoriaLeido, conPanelAsesoria, seccion]);

  // Avisos y plan: se recalculan al cambiar de sección, así un aviso resuelto
  // desaparece sin recargar. Lo que falle (permiso, red) simplemente no avisa.
  const cargarAvisos = useCallback(async (): Promise<void> => {
    const [cuotas, plazas, mfa, resumen, facturas, autorizacion, suscripcion, compras] = await Promise.all([
      api.cuotasPresupuestos(),
      api.plazasClientes(),
      api.estadoMfa(),
      api.resumenPresupuestos(),
      api.usoFacturas(),
      api.autorizacionAeat(),
      api.suscripcion(),
      // OD-0054 D-05. Un rol que no ve compras recibe 403, y entonces no se avisa.
      apiCompras.avisos(),
    ]);
    // Se recalcula con lo demas: tras subir de plan, el boton de ese plan
    // desaparece en cuanto el servidor deja de ofrecerlo.
    setOferta(planesQueOfrecer(suscripcion.ok ? suscripcion.datos : null));
    setOfertaDirecta(abrePagoDirecto(suscripcion.ok ? suscripcion.datos : null));
    setPrueba(suscripcion.ok ? suscripcion.datos.prueba : null);
    setClientesOcupados(plazas.ok ? plazas.datos.used : null);
    // OD-0048-ENMIENDA-02 F-02: las tareas vencidas, solo en Business (fuera, la
    // API contesta 402 y no hay nada que contar). Una lectura más por carga.
    const conCrm = cuotas.ok && cuotas.datos.planVersion !== undefined && cuotas.datos.planVersion.startsWith("BUSINESS_");
    // OD-0062 E-11: las facturas vencidas, de la MISMA lectura que Cobros
    // pendientes con «Vencidas» (la API suma). Va a la vez que las tareas.
    const vencidasPedidas = conCrm ? api.cobrosPendientes({ estado: "OVERDUE", cliente: null, desde: null, hasta: null }) : Promise.resolve(null);
    const pendientes = conCrm ? await api.listarActividades("PENDING") : null;
    const tareasVencidas = pendientes !== null && pendientes.ok ? contarVencidas(pendientes.datos.actividades, fechaEnDias(new Date(), 0)) : null;
    const tareasHoy = pendientes !== null && pendientes.ok ? contarDeHoy(pendientes.datos.actividades, fechaEnDias(new Date(), 0)) : null;
    const vencidas = await vencidasPedidas;
    const facturasVencidas = vencidas !== null && vencidas.ok ? { facturas: vencidas.datos.facturas, pendiente: vencidas.datos.pendiente } : null;
    if (cuotas.ok) {
      setPlan(nombrePlan(cuotas.datos.planVersion ?? null));
      setEsBusiness(cuotas.datos.planVersion === undefined ? null : cuotas.datos.planVersion.startsWith("BUSINESS_"));
    }
    // Paso 7: lo dice el contador de la API (`usage/invoices`), no la lista de
    // facturas. Si no viene, se queda en "no se sabe": decir "pendiente" a
    // quien ya ha emitido es tan falso como lo contrario.
    const facturaEmitida = facturas.ok && facturas.datos.issued !== null ? facturas.datos.issued.ever : null;
    if (facturaEmitida !== null) setTieneFacturaEmitida(facturaEmitida);
    setUsoFacturas(facturas.ok ? facturas.datos : null);
    // `null` si no se sabe: un fallo de red no dice que esté autorizado.
    const autorizacionLeida = autorizacion.ok ? autorizacion.datos.estado : null;
    const mfaLeido = mfa.ok ? mfa.datos.totp === "ACTIVE" : null;
    setEstadoAutorizacion(autorizacionLeida);
    setMfaActivo(mfaLeido);
    setAvisos(
      calcularAvisos({
        // LA lista de pasos del alta: la misma de Primeros pasos y del asistente.
        alta: {
          emisor: estadoEmisor,
          sii: siiEmisor,
          autorizacion: autorizacionLeida,
          mfaActivo: mfaLeido,
          tieneClientes: plazas.ok ? plazas.datos.used > 0 : null,
          tieneFacturaEmitida: facturaEmitida,
        },
        cuotas: cuotas.ok ? cuotas.datos : null,
        plazas: plazas.ok ? plazas.datos : null,
        mfa: mfa.ok ? mfa.datos : null,
        enviados: resumen.ok ? (resumen.datos.byStatus.SENT ?? 0) : null,
        remision: facturas.ok ? facturas.datos.remision : null,
        anticiposSinEmitir: facturas.ok ? facturas.datos.pendingAdvanceDrafts : null,
        pagosSinFactura: compras.ok ? compras.datos : null,
        tareasVencidas,
        tareasHoy,
        facturasVencidas,
      }),
    );
  }, [estadoEmisor, siiEmisor]);

  useEffect(() => {
    void cargarAvisos();
  }, [cargarAvisos, seccion, ajuste]);

  // Abrir una oportunidad empieza ARRIBA: la ventana heredaba el desplazamiento
  // de la lista, y la ficha se abría por la mitad.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [vistaOportunidades]);

  const irA = (s: Seccion): void => {
    if (s === "clientes") setVistaClientes({ tipo: "lista" });
    if (s === "productos") setVistaProductos({ tipo: "lista" });
    if (s === "presupuestos") setVistaPresupuestos({ tipo: "lista" });
    if (s === "facturas") setVistaFacturas({ tipo: "lista" });
    if (s === "oportunidades") {
      setVistaOportunidades({ tipo: "lista" });
      setPestanaOportunidades("OPEN");
      setBusquedaOportunidades("");
    }
    if (s === "compras") {
      setVistaCompras({ tipo: "lista", pestana: "recibidas" });
      setFiltrosCompras(SIN_FILTROS_COMPRAS);
      setFiltrosPresupuestosProveedor(SIN_FILTROS_PRESUPUESTOS);
    }
    setPresupuestoInicial(null);
    if (s === "inicio" || s === "configuracion") void cargarClientes();
    setSeccion(s);
  };

  const irAAjuste = (a: Ajuste): void => {
    setAjuste(a);
    setSeccion("configuracion");
  };

  const irAPlan = (p: PlanDePago): void => {
    setPlanPulsado(p);
    irAAjuste("plan");
  };

  /*
    Pulsar Factura+ o Business en «Mejora tu plan» abre ya la pantalla de pago
    de Stripe (propietario, 2026-10-21): antes llevaba a Plan y pago y habia
    que pulsar otra vez. Es la MISMA llamada que el boton de esa pantalla, con
    las mismas comprobaciones en la API; lo unico que cambia es que no se
    pasa por ella cuando no hace falta.

    Si la API se niega (faltan los datos de facturacion, un fallo), se cae a
    la pantalla del plan con lo que haya contestado, que es donde se arregla.
  */
  const elegirPlan = async (p: PlanDePago): Promise<void> => {
    setFalloDelPago(null);
    if (!ofertaDirecta || !hayConexion) {
      irAPlan(p);
      return;
    }
    setAbriendoPago(p);
    const r = await api.contratar(p);
    if (r.ok) {
      olvidarPlanElegido();
      window.location.href = r.datos.url;
      return;
    }
    setAbriendoPago(null);
    setFalloDelPago(r.error?.message ?? null);
    irAPlan(p);
  };

  // Al salir de la pantalla del plan, lo pulsado deja de valer: volver por el
  // menú no tiene que encontrarse un plan elegido de antes.
  useEffect(() => {
    if (seccion !== "configuracion" || ajuste !== "plan") {
      setPlanPulsado(null);
      setFalloDelPago(null);
    }
  }, [seccion, ajuste]);

  const irAAviso = (d: DestinoAviso): void => {
    // La autorización no es una pantalla: es el trámite de tres pasos, y está
    // en el diálogo del alta (`OD-0039` E-11).
    if (d === "autorizacion") return setPuertaAbierta(true);
    if (d === "emisor" || d === "seguridad") irAAjuste(d);
    else if (d === "cobros") {
      setSeccion("facturas");
      setVistaFacturas({ tipo: "pendientes", filtro: "OVERDUE" });
    } else if (d === "compras") {
      // Los pagos sin factura están en «Pagos pendientes», no en la lista de recibidas.
      setSeccion("compras");
      setVistaCompras({ tipo: "lista", pestana: "pendientes" });
    } else irA(d);
  };

  const nuevoPresupuesto = (): void => {
    setPresupuestoInicial(null);
    setSeccion("presupuestos");
    setVistaPresupuestos({ tipo: "nuevo" });
  };

  /*
    EL BOTON GRANDE DEL MOVIL. Hasta el 2026-10-15 creaba un PRESUPUESTO en una
    aplicacion de FACTURACION: la accion central no era la del producto.

    Lleva a la pantalla de factura nueva aunque la cuenta no pueda emitir
    todavia, Y ESO ES DELIBERADO: esa pantalla tiene su propio control previo,
    que dice EXACTAMENTE que falta (OD-0039 E-11, "la pantalla de factura tiene
    su propio control previo"). Es mejor que un boton que no hace nada, o que
    desaparece sin decir por que.
  */
  const nuevaFactura = (): void => {
    setSeccion("facturas");
    setVistaFacturas({ tipo: "nuevo" });
  };

  async function salir(): Promise<void> {
    setSaliendo(true);
    // Las cifras del panel de la gestoria no sobreviven a la sesion.
    vaciarCache(almacenDeLaPestana());
    // Aunque falle la red se vuelve a la entrada: quedarse en una pantalla de
    // sesión que el usuario ha pedido cerrar es peor. La cookie caduca sola.
    await api.logout();
    setSaliendo(false);
    onSalir();
  }

  const contenido = useRef<HTMLDivElement>(null);
  const primeraVez = useRef(true);

  useEffect(() => {
    // Al entrar no se mueve el foco: el usuario aún no ha pedido nada.
    if (primeraVez.current) {
      primeraVez.current = false;
      return;
    }
    contenido.current?.focus();
  }, [seccion, ajuste]);

  // OD-0051 (propietario, 2026-10-22): «Cobros pendientes» cuelga de
  // Facturas en el lateral, siempre a la vista, en vez de esconderse tras el
  // enlace de la cifra de lo pendiente. Un solo apartado, asi que el lateral
  // crece una fila y no las seis del submenu que se quito el 2026-10-19.
  // Fuera de Business se ve igual: la pantalla explica que es de Business,
  // como Oportunidades, Tareas e Informes.
  const enPendientes = seccion === "facturas" && vistaFacturas.tipo === "pendientes";
  const opcion = (o: Opcion) => (
    <li key={o.id}>
      <button
        type="button"
        className="menu-opcion"
        aria-current={seccion === o.id && !(o.id === "facturas" && enPendientes) ? "page" : undefined}
        onClick={() => irA(o.id)}
      >
        <Icono nombre={o.icono} />
        <span>{o.etiqueta}</span>
      </button>
      {o.id === "facturas" ? (
        <ul className="submenu">
          <li>
            <button
              type="button"
              className="submenu-opcion"
              aria-current={enPendientes ? "page" : undefined}
              onClick={() => {
                irA("facturas");
                setVistaFacturas({ tipo: "pendientes" });
              }}
            >
              {t.panel.cobrosPendientes}
            </button>
          </li>
        </ul>
      ) : null}
    </li>
  );

  return (
    <div className="panel">
      {/*
        OD-0039 con la enmienda E-11: el aviso del alta. Se dibuja sobre el
        panel al entrar y SE PUEDE CERRAR. Cerrarlo no completa nada: quien
        decide si se puede emitir es el control previo del servidor, que no
        sabe ni le importa si este dialogo esta abierto.

        El componente decide solo si hay algo que contar; aqui solo se dice
        si se ve.
      */}
      {puertaAbierta ? (
        <PuertaAutorizacion
          hayConexion={hayConexion}
          onSesionCaducada={onSalir}
          onCerrar={() => {
            setPuertaAbierta(false);
            // Auditoría del 2026-10-29, hallazgo 5: lo guardado dentro tiene
            // que verse fuera SIN RECARGAR. Antes solo se recalculaban los
            // avisos, con el emisor de antes de abrir el asistente.
            void cargarEmisor();
            void cargarAvisos();
          }}
          onCambio={() => {
            void cargarEmisor();
            void cargarAvisos();
          }}
          onIrADatosFiscales={() => {
            // Cierra el asistente: quedarse encima de la pantalla a la
            // que manda es la trampa que E-11 retira.
            setPuertaAbierta(false);
            irAAjuste("emisor");
          }}
        />
      ) : null}
      {/* OD-0063 D-05: si en otra pestaña se cambia de empresa, esta se tapa. */}
      <AvisoDeOtraPestana organizationId={sesion.organizationId} />
      <aside className="barra-lateral">
        <h1 className="marca">{t.app.marca}</h1>
        <div className="tarjeta-empresa">
          <span className="avatar" aria-hidden="true">
            {iniciales(nombreEmpresa)}
          </span>
          <span className="tarjeta-empresa-texto">
            <span className="tarjeta-empresa-nombre">{nombreOrganizacion ?? nombreEmpresa ?? t.panel.tuEmpresa}</span>
            {plan !== null ? <span className="tarjeta-empresa-plan">{plan}</span> : null}
          </span>
        </div>
        <SelectorDeEmpresa id="lateral" onCambiada={onCambioDeEmpresa} onSesionCaducada={onSalir} />
        <nav className="menu" aria-label={t.panel.navegacion}>
          {conPanelAsesoria ? (
            <>
              <p className="menu-grupo">{t.panel.grupoAsesoria}</p>
              <ul>{opcion(OPCION_GESTORIA)}</ul>
            </>
          ) : null}
          <p className="menu-grupo">{t.panel.grupoMenu}</p>
          <ul>{MENU.map(opcion)}</ul>
          <p className="menu-grupo">{t.panel.grupoPreferencias}</p>
          <ul>
            {PREFERENCIAS.map((o) => (
              <li key={o.id}>
                {/*
                  Sin submenu: los seis apartados de Configuracion ya son las
                  pestañas de arriba de la pantalla. Salian DOS veces, y con el
                  submenu abierto el lateral medía unos 1.030 px, así que en un
                  portátil su pie quedaba por debajo del borde (auditoria de
                  diseno, 2026-10-19).
                */}
                <button
                  type="button"
                  className="menu-opcion"
                  aria-current={seccion === o.id ? "page" : undefined}
                  onClick={() => irA(o.id)}
                >
                  <Icono nombre={o.icono} />
                  <span>{o.etiqueta}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <MejoraDePlan oferta={ofertaALaVista} variante="lateral" abriendo={abriendoPago} onElegir={(p) => void elegirPlan(p)} />
        <button type="button" className="cerrar-sesion" onClick={() => void salir()} disabled={saliendo}>
          <Icono nombre="salir" />
          <span>{saliendo ? t.cuenta.saliendo : t.cuenta.salir}</span>
        </button>
      </aside>

      <div className="panel-principal">
        <div className="barra-superior">
          {/* Q-05 (OM HAC/1177/2024 art. 2.d): el obligado se ve siempre, también
              en el móvil, donde no está la tarjeta de la empresa. */}
          <span className="empresa-movil" title={nombreEmpresa ?? undefined}>
            <span className="avatar" aria-hidden="true">
              {iniciales(nombreEmpresa)}
            </span>
            <span className="empresa-movil-nombre">{nombreOrganizacion ?? nombreEmpresa ?? t.panel.tuEmpresa}</span>
          </span>
          <BuscadorGlobal
            onAbrirCliente={(id) => {
              setSeccion("clientes");
              setVistaClientes({ tipo: "ficha", id });
            }}
            onAbrirProducto={(id) => {
              setSeccion("productos");
              setVistaProductos({ tipo: "ficha", id });
            }}
            onAbrirPresupuesto={(id) => {
              setSeccion("presupuestos");
              setVistaPresupuestos({ tipo: "ficha", id });
            }}
            onAbrirFactura={(id) => {
              setSeccion("facturas");
              setVistaFacturas({ tipo: "ficha", id });
            }}
          />
          {!hayConexion ? <span className="entorno">{t.conexion.sinConexion}</span> : null}
          <CampanaAvisos avisos={avisos} onIr={irAAviso} />
        </div>

        <div className="panel-contenido" ref={contenido} tabIndex={-1}>
          {/* OD-0063 D-05: dentro de la empresa de un cliente, siempre a la vista. */}
          {sesion.rol === "ASESORIA" ? (
            <FranjaDeAsesoria empresa={nombreEmpresa} onCambioDeEmpresa={onCambioDeEmpresa} onSesionCaducada={onSalir} />
          ) : null}
          {seccion === "gestoria" && panelAsesoria !== null ? (
            <PantallaPanelAsesoria
              sesion={sesion}
              panel={panelAsesoria}
              hayConexion={hayConexion}
              onCambioDeEmpresa={onCambioDeEmpresa}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {prueba !== null && !(seccion === "configuracion" && ajuste === "plan") ? (
            <FranjaDePrueba prueba={prueba} clientes={clientesOcupados} onElegirPlan={() => irAAjuste("plan")} />
          ) : null}
          {seccion === "inicio" ? <MejoraDePlan oferta={ofertaALaVista} variante="movil" abriendo={abriendoPago} onElegir={(p) => void elegirPlan(p)} /> : null}
          {/* OD-0063 D-08: quien llego por el enlace de su gestoria. Solo users.manage y con el programa encendido. */}
          {seccion === "inicio" && programaAsesorias ? <InvitacionDeAsesoria hayConexion={hayConexion} /> : null}
          {/* OD-0063 D-04 (enmienda 2026-10-28): la invitacion del cliente a su gestoria, desde las dos puntas. */}
          {seccion === "inicio" && programaAsesorias ? <AsesoriaQueHaEntrado hayConexion={hayConexion} /> : null}
          {seccion === "inicio" && programaAsesorias ? <InvitacionDeTuCliente onIrAAsesoria={() => irAAjuste("asesoria")} /> : null}
          {/* OD-0067 D-05: la asesoria ha escrito y nadie lo ha leido. */}
          {seccion === "inicio" && conversacion !== null ? <AvisoDeConversacion noLeidos={conversacion.noLeidos} onAbrir={() => irAAjuste("asesoria")} /> : null}
          {seccion === "inicio" ? (
            <PantallaInicio
              esBusiness={esBusiness}
              usoFacturas={usoFacturas}
              avisos={avisos}
              onIrAviso={irAAviso}
              onIrAConfiguracion={() => irAAjuste("pasos")}
              onIrAPresupuestos={() => irA("presupuestos")}
              onNuevoPresupuesto={nuevoPresupuesto}
              onAbrirPresupuesto={(id) => {
                setSeccion("presupuestos");
                setVistaPresupuestos({ tipo: "ficha", id });
              }}
              onIrAOportunidades={() => irA("oportunidades")}
              onNuevaOportunidad={() => {
                setSeccion("oportunidades");
                setVistaOportunidades({ tipo: "nuevo", clienteId: null });
              }}
              onAbrirOportunidad={(id) => {
                setSeccion("oportunidades");
                setVistaOportunidades({ tipo: "ficha", id });
              }}
              onIrAInformes={() => irA("informes")}
            />
          ) : null}
          {seccion === "clientes" && vistaClientes.tipo === "lista" ? (
            <PantallaClientes
              onAbrir={(id) => setVistaClientes({ tipo: "ficha", id })}
              onNuevo={() => setVistaClientes({ tipo: "nuevo" })}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "clientes" && vistaClientes.tipo !== "lista" ? (
            <PantallaCliente
              // La key fuerza un formulario nuevo (y una clave de idempotencia nueva) por cliente.
              key={vistaClientes.tipo === "ficha" ? vistaClientes.id : "nuevo"}
              id={vistaClientes.tipo === "ficha" ? vistaClientes.id : null}
              hayConexion={hayConexion}
              onVolver={() => {
                setVistaClientes({ tipo: "lista" });
                void cargarClientes();
              }}
              onAbrir={(id) => setVistaClientes({ tipo: "ficha", id })}
              onAbrirOportunidad={(id) => {
                setSeccion("oportunidades");
                setVistaOportunidades({ tipo: "ficha", id });
              }}
              onNuevaOportunidad={(clienteId) => {
                setSeccion("oportunidades");
                setVistaOportunidades({ tipo: "nuevo", clienteId });
              }}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "oportunidades" && vistaOportunidades.tipo === "lista" ? (
            <PantallaOportunidades
              pestana={pestanaOportunidades}
              onPestana={setPestanaOportunidades}
              busqueda={busquedaOportunidades}
              onBusqueda={setBusquedaOportunidades}
              onAbrir={(id, cualificar) => setVistaOportunidades(cualificar === true ? { tipo: "ficha", id, invitar: "movida" } : { tipo: "ficha", id })}
              onNueva={() => setVistaOportunidades({ tipo: "nuevo", clienteId: null })}
              onVerPlan={() => irAAjuste("plan")}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "informes" ? (
            <PantallaInformesFiscales
              hayConexion={hayConexion}
              onVerPlan={() => irAAjuste("plan")}
              onAbrirFactura={(id) => {
                setSeccion("facturas");
                setVistaFacturas({ tipo: "ficha", id });
              }}
              onAbrirCompra={(id) => {
                setSeccion("compras");
                setVistaCompras({ tipo: "compra", id, proveedorId: null });
              }}
              onIrACompras={() => {
                setSeccion("compras");
                setVistaCompras({ tipo: "lista", pestana: "recibidas" });
              }}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "tareas" ? (
            <PantallaTareas
              onVerPlan={() => irAAjuste("plan")}
              onAbrirPresupuesto={(id) => {
                setSeccion("presupuestos");
                setVistaPresupuestos({ tipo: "ficha", id });
              }}
              onAbrirFactura={(id) => {
                setSeccion("facturas");
                setVistaFacturas({ tipo: "ficha", id });
              }}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "oportunidades" && vistaOportunidades.tipo !== "lista" ? (
            <PantallaOportunidad
              key={vistaOportunidades.tipo === "ficha" ? vistaOportunidades.id : "nueva"}
              id={vistaOportunidades.tipo === "ficha" ? vistaOportunidades.id : null}
              clienteInicial={vistaOportunidades.tipo === "nuevo" ? vistaOportunidades.clienteId : null}
              invitar={vistaOportunidades.tipo === "ficha" ? (vistaOportunidades.invitar ?? null) : null}
              hayConexion={hayConexion}
              onVolver={() => setVistaOportunidades({ tipo: "lista" })}
              onAbrir={(id, recienCreada) => setVistaOportunidades(recienCreada === true ? { tipo: "ficha", id, invitar: "creada" } : { tipo: "ficha", id })}
              onCrearPresupuesto={(clienteId, concepto, oportunidadId) => {
                setPresupuestoInicial({ clienteId, concepto, oportunidadId });
                setSeccion("presupuestos");
                setVistaPresupuestos({ tipo: "nuevo" });
              }}
              onAbrirPresupuesto={(quoteId) => {
                setPresupuestoInicial(null);
                setSeccion("presupuestos");
                setVistaPresupuestos({ tipo: "ficha", id: quoteId });
              }}
              onVerPlan={() => irAAjuste("plan")}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "productos" && vistaProductos.tipo === "lista" ? (
            <PantallaProductos
              onAbrir={(id) => setVistaProductos({ tipo: "ficha", id })}
              onNuevo={() => setVistaProductos({ tipo: "nuevo" })}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "productos" && vistaProductos.tipo !== "lista" ? (
            <PantallaProducto
              // La key fuerza un formulario nuevo (y una clave de idempotencia nueva) por producto.
              key={vistaProductos.tipo === "ficha" ? vistaProductos.id : "nuevo"}
              id={vistaProductos.tipo === "ficha" ? vistaProductos.id : null}
              hayConexion={hayConexion}
              onVolver={() => setVistaProductos({ tipo: "lista" })}
              onAbrir={(id) => setVistaProductos({ tipo: "ficha", id })}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "presupuestos" && vistaPresupuestos.tipo === "lista" ? (
            <PantallaPresupuestos
              onAbrir={(id) => setVistaPresupuestos({ tipo: "ficha", id })}
              onNuevo={() => setVistaPresupuestos({ tipo: "nuevo" })}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "presupuestos" && vistaPresupuestos.tipo !== "lista" ? (
            <PantallaPresupuesto
              // La key fuerza un formulario nuevo (y una clave de idempotencia nueva).
              key={vistaPresupuestos.tipo === "ficha" ? vistaPresupuestos.id : "nuevo"}
              id={vistaPresupuestos.tipo === "ficha" ? vistaPresupuestos.id : null}
              inicial={vistaPresupuestos.tipo === "nuevo" ? presupuestoInicial : null}
              hayConexion={hayConexion}
              onVolver={() => setVistaPresupuestos({ tipo: "lista" })}
              onAbrir={(id) => setVistaPresupuestos({ tipo: "ficha", id })}
              onIrAEmisor={() => irAAjuste("emisor")}
              onAbrirFactura={(fid) => {
                setSeccion("facturas");
                setVistaFacturas({ tipo: "ficha", id: fid });
              }}
              onAbrirOportunidad={(oid) => {
                setSeccion("oportunidades");
                setVistaOportunidades({ tipo: "ficha", id: oid });
              }}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "facturas" && vistaFacturas.tipo === "lista" ? (
            <PantallaFacturas
              // OD-0036: la exportación para la gestoría se abre desde su botón, junto a «Nuevo borrador».
              hayConexion={hayConexion}
              onAbrir={(fid) => setVistaFacturas({ tipo: "ficha", id: fid })}
              onNueva={() => setVistaFacturas({ tipo: "nuevo" })}
              onIrAPresupuestos={() => irA("presupuestos")}
              onVerPendientes={() => setVistaFacturas({ tipo: "pendientes" })}
              onVerImportadas={() => setVistaFacturas({ tipo: "importadas" })}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "facturas" && vistaFacturas.tipo === "importadas" ? (
            <PantallaFacturasImportadas onVolver={() => setVistaFacturas({ tipo: "lista" })} />
          ) : null}
          {seccion === "facturas" && vistaFacturas.tipo === "pendientes" ? (
            <PantallaCobrosPendientes
              key={vistaFacturas.filtro ?? "PENDING"}
              filtroInicial={vistaFacturas.filtro ?? "PENDING"}
              hayConexion={hayConexion}
              onAbrir={(fid) => setVistaFacturas({ tipo: "ficha", id: fid })}
              onVolver={() => setVistaFacturas({ tipo: "lista" })}
              onVerPlan={() => irAAjuste("plan")}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "facturas" && (vistaFacturas.tipo === "ficha" || vistaFacturas.tipo === "nuevo") ? (
            <PantallaFactura
              // La key fuerza un formulario nuevo (y una clave de idempotencia nueva).
              key={vistaFacturas.tipo === "ficha" ? vistaFacturas.id : "nuevo"}
              id={vistaFacturas.tipo === "ficha" ? vistaFacturas.id : null}
              hayConexion={hayConexion}
              onVolver={() => setVistaFacturas({ tipo: "lista" })}
              onAbrir={(fid) => setVistaFacturas({ tipo: "ficha", id: fid })}
              onIrAEmisor={() => irAAjuste("emisor")}
              // Lo que falta para emitir lo dice la API; si es la autorizacion
              // o el segundo factor, el tramite esta en el dialogo del alta.
              onAbrirPuerta={() => setPuertaAbierta(true)}
              onAbrirCliente={(cid) => {
                setSeccion("clientes");
                setVistaClientes({ tipo: "ficha", id: cid });
              }}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "compras" && vistaCompras.tipo === "lista" ? (
            <PantallaCompras
              hayConexion={hayConexion}
              pestana={vistaCompras.pestana}
              onPestana={(p) => setVistaCompras({ tipo: "lista", pestana: p })}
              filtros={filtrosCompras}
              onFiltros={setFiltrosCompras}
              onAbrirCompra={(id) => setVistaCompras({ tipo: "compra", id, proveedorId: null })}
              onApuntarPago={(id) => setVistaCompras({ tipo: "compra", id, proveedorId: null, alPago: true })}
              onNuevaCompra={() => setVistaCompras({ tipo: "compra", id: null, proveedorId: null })}
              filtrosPresupuestos={filtrosPresupuestosProveedor}
              onFiltrosPresupuestos={setFiltrosPresupuestosProveedor}
              onAbrirPresupuesto={(id) => setVistaCompras({ tipo: "presupuesto", id })}
              onNuevoPresupuesto={() => setVistaCompras({ tipo: "presupuesto", id: null })}
              onAnotarFacturaDe={(f) =>
                setVistaCompras({ tipo: "compra", id: null, proveedorId: f.supplierId, presupuesto: { id: f.id, referencia: f.reference } })
              }
              onAbrirProveedor={(id) => setVistaCompras({ tipo: "proveedor", id })}
              onNuevoProveedor={() => setVistaCompras({ tipo: "proveedor", id: null })}
              onVerPlan={() => irAAjuste("plan")}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "compras" && vistaCompras.tipo === "compra" ? (
            <PantallaCompra
              // La key fuerza un formulario nuevo (y una clave de idempotencia nueva).
              key={vistaCompras.id ?? `nueva-${vistaCompras.proveedorId ?? ""}-${vistaCompras.presupuesto?.id ?? ""}`}
              id={vistaCompras.id}
              proveedorInicial={vistaCompras.proveedorId}
              presupuestoInicial={vistaCompras.presupuesto ?? null}
              alPago={vistaCompras.alPago === true}
              hayConexion={hayConexion}
              onVolver={() => {
                const volverA = vistaCompras.volverA ?? vistaCompras.presupuesto?.id;
                setVistaCompras(volverA !== undefined ? { tipo: "presupuesto", id: volverA } : { tipo: "lista", pestana: "recibidas" });
              }}
              onAbrirProveedor={(id) => setVistaCompras({ tipo: "proveedor", id })}
              onAbrirPresupuesto={(id) => setVistaCompras({ tipo: "presupuesto", id })}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "compras" && vistaCompras.tipo === "presupuesto" ? (
            <PantallaPresupuestoProveedor
              key={vistaCompras.id ?? "nuevo"}
              id={vistaCompras.id}
              hayConexion={hayConexion}
              onVolver={() => setVistaCompras({ tipo: "lista", pestana: "presupuestos" })}
              onAbrirCompra={(id) => setVistaCompras({ tipo: "compra", id, proveedorId: null, volverA: vistaCompras.id ?? undefined })}
              onAbrirProveedor={(id) => setVistaCompras({ tipo: "proveedor", id })}
              onAnotarFactura={(proveedorId, desde) => setVistaCompras({ tipo: "compra", id: null, proveedorId, presupuesto: desde })}
              onVerEnRecibidas={(presupuesto) => {
                setFiltrosCompras({ ...SIN_FILTROS_COMPRAS, presupuesto });
                setVistaCompras({ tipo: "lista", pestana: "recibidas" });
              }}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "compras" && vistaCompras.tipo === "proveedor" ? (
            <PantallaProveedor
              key={vistaCompras.id ?? "nuevo"}
              id={vistaCompras.id}
              hayConexion={hayConexion}
              onVolver={() => setVistaCompras({ tipo: "lista", pestana: "proveedores" })}
              onAbrirCompra={(id) => setVistaCompras({ tipo: "compra", id, proveedorId: null })}
              onNuevaCompra={(proveedorId) => setVistaCompras({ tipo: "compra", id: null, proveedorId })}
              onVerPlan={() => irAAjuste("plan")}
              onSesionCaducada={onSalir}
            />
          ) : null}
          {seccion === "soporte" ? <PantallaSoporte /> : null}
          {seccion === "mas" ? (
            <nav className="menu menu-mas" aria-label={t.panel.mas}>
              <ul>
                {/* Lo que no cabe en la barra: Compras, Clientes, Productos, Oportunidades, Tareas, Informes y
                    las dos preferencias. Tiene que coincidir con lo que la
                    barra marca como «Mas», o un destino se queda sin ninguna via. */}
                {[
                  conPanelAsesoria ? OPCION_GESTORIA : undefined,
                  MENU[3],
                  MENU[4],
                  MENU[5],
                  MENU[6],
                  MENU[7],
                  MENU[8],
                  PREFERENCIAS[0],
                  PREFERENCIAS[1],
                ].map((o) => (o === undefined ? null : opcion(o)))}
                <li>
                  <button type="button" className="cerrar-sesion" onClick={() => void salir()} disabled={saliendo}>
                    <Icono nombre="salir" />
                    <span>{saliendo ? t.cuenta.saliendo : t.cuenta.salir}</span>
                  </button>
                </li>
              </ul>
            </nav>
          ) : null}
          {seccion === "configuracion" ? (
            <>
              <h2 className="titulo-seccion">{t.panel.configuracion}</h2>
              <div className="pestanas" role="group" aria-label={t.panel.configuracionSecciones}>
                {ajustes.filter((a) => a.id !== "equipo" || gestionaEquipo).map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="pestana"
                    aria-pressed={ajuste === a.id}
                    onClick={() => setAjuste(a.id)}
                  >
                    <Icono nombre={a.icono} />
                    <span>{a.etiqueta}</span>
                  </button>
                ))}
              </div>
              {ajuste === "pasos" ? (
                <PantallaConfiguracion
                  alta={{
                    emisor: estadoEmisor,
                    sii: siiEmisor,
                    autorizacion: estadoAutorizacion,
                    mfaActivo,
                    tieneClientes,
                    tieneFacturaEmitida,
                  }}
                  onIrAEmisor={() => setAjuste("emisor")}
                  onAbrirAsistente={() => setPuertaAbierta(true)}
                  onIrASeguridad={() => setAjuste("seguridad")}
                  onIrAClientes={() => irA("clientes")}
                  onIrAFacturas={() => irA("facturas")}
                />
              ) : null}
              {ajuste === "emisor" ? (
                <PantallaEmisor
                  hayConexion={hayConexion}
                  onIrASeguridad={() => setAjuste("seguridad")}
                  onSesionCaducada={onSalir}
                  onCambio={() => void cargarEmisor()}
                />
              ) : null}
              {ajuste === "autorizacion" ? (
                <PantallaAutorizacionAeat
                  hayConexion={hayConexion}
                  onSesionCaducada={onSalir}
                  onCambio={() => void cargarEmisor()}
                />
              ) : null}
              {ajuste === "importar" ? <PantallaImportar hayConexion={hayConexion} /> : null}
              {ajuste === "seguridad" ? <PantallaSeguridad hayConexion={hayConexion} onSesionCaducada={onSalir} /> : null}
              {ajuste === "cuenta" ? <PantallaCuenta sesion={sesion} empresa={nombreEmpresa} plan={plan} onSalir={onSalir} onCambioDeEmpresa={onCambioDeEmpresa} /> : null}
              {ajuste === "equipo" && gestionaEquipo ? (
                <PantallaEquipo hayConexion={hayConexion} onSesionCaducada={onSalir} onIrAPlan={() => setAjuste("plan")} />
              ) : null}
              {ajuste === "asesoria" && programaAsesorias ? <PantallaAsesoria hayConexion={hayConexion} /> : null}
              {ajuste === "asesoria" && conversacion !== null ? (
                <ConversacionConLaAsesoria
                  organizationId={null}
                  hayConexion={hayConexion}
                  onSesionCaducada={onSalir}
                  onLeida={() => setConversacion({ noLeidos: 0 })}
                />
              ) : null}
              {ajuste === "plan" ? (
                <PantallaPlan
                  hayConexion={hayConexion}
                  plan={plan}
                  vuelta={vueltaDePago}
                  // Lo pulsado en el panel manda sobre lo elegido en el sitio:
                  // es lo último que ha pedido la persona.
                  planElegido={planPulsado ?? planElegido}
                  falloInicial={falloDelPago}
                  // El plan acaba de cambiar: se relee para que el nombre de
                  // arriba a la izquierda deje de ser el viejo.
                  onPlanCambiado={() => void cargarAvisos()}
                />
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      <nav className="barra-inferior" aria-label={t.panel.navegacionMovil}>
        {MENU.slice(0, 2).map((o) => (
          <button
            key={o.id}
            type="button"
            className="barra-inferior-opcion"
            aria-current={seccion === o.id ? "page" : undefined}
            onClick={() => irA(o.id)}
          >
            <Icono nombre={o.icono} />
            <span>{o.etiqueta}</span>
          </button>
        ))}
        <button type="button" className="barra-inferior-crear" aria-label={t.panel.nuevaFactura} onClick={nuevaFactura}>
          <Icono nombre="mas" />
        </button>
        {/*
          FACTURAS en la barra, y Clientes a «Mas». Estaba al reves: en una
          aplicacion de facturacion, el sustantivo que le da nombre vivia a dos
          toques detras de un menu. Se cede el hueco de Clientes y no el de
          Presupuestos porque un cliente se crea de uvas a peras -y se alcanza
          desde el formulario de cualquier factura o presupuesto-, mientras que
          las dos listas son destino DIARIO.
        */}
        {[MENU[2], { id: "mas" as const, etiqueta: t.panel.mas, icono: "menu" as const }].map((o) =>
          o === undefined ? null : (
            <button
              key={o.id}
              type="button"
              className="barra-inferior-opcion"
              aria-current={
                seccion === o.id ||
                (o.id === "mas" &&
                  (seccion === "compras" ||
                    seccion === "gestoria" ||
                    seccion === "clientes" ||
                    seccion === "productos" ||
                    seccion === "oportunidades" ||
                    seccion === "tareas" ||
                    seccion === "informes" ||
                    seccion === "configuracion" ||
                    seccion === "soporte"))
                  ? "page"
                  : undefined
              }
              onClick={() => irA(o.id)}
            >
              <Icono nombre={o.icono} />
              <span>{o.etiqueta}</span>
            </button>
          ),
        )}
      </nav>
    </div>
  );
}
